import { GatewayContact, GatewayEntreprise } from "@conformite/gateway";
import Download from "@endya-conformite/ui-kit/src/assets/icons/download.svg?react";
import RightArrow from "@endya-conformite/ui-kit/src/assets/icons/arrow-right.svg?react";
import {
  displayErrorToast,
  displayInfoToast,
  displaySucessToast,
  FormField,
  FormGrid,
  FormRow,
  FormTitle,
  SkeletonCard,
} from "@endya-conformite/ui-kit";
import { PrismicProvider } from "@prismicio/react";
import { GatewayImportExportAssujettiAPI } from "@src/api/importExportAssujetti.api";
import {
  assujettiExcelExportTemplate,
  assujettiExcelTemplate,
} from "@src/assujettis/ImportExport/AssujettiExcelTemplate";
import {
  addAssujettiDataValidationToSheet,
  downloadSheetFromWorkbook,
  importWorkBookFromFile,
  insertDataInSheet,
} from "@src/helper/excel/excel.export.helper";
import { transformAPIRowsForToRows } from "@src/assujettis/ImportExport/Export/ExportAssujettiExcelMapping";
import { useAssujetisAvancement } from "@src/assujettis/useAssujettiAvancement";
import { ActualiteList } from "@src/cms/Actualite/ActualiteList";
import { useActualite } from "@src/cms/Actualite/useActualite";
import { CmsHeader } from "@src/cms/CmsHeader";
import { LeSaviezVous } from "@src/cms/LeSaviezVous/LeSaviezVous";
import { prismicClient } from "@src/cms/prismic";
import { MainPageTemplate } from "@src/components/MainPageTemplate/MainPageTemplate";
import { useIsAllowed } from "@src/session/useIsAllowed";
import { DownloadAttestationCard } from "@src/societes/attestation/DownloadAttestationCard";
import { useDownloadAttestation } from "@src/societes/attestation/useDownloadAttestation";
import { useEntrepriseAvancement } from "@src/societes/useEntrepriseAvancement";
import { useAssujetti } from "@src/store/store.assujetti";
import { useEntreprises } from "@src/store/store.entreprise";
import { StoreEntryFetchStatus } from "@src/store/store";
import { useMe } from "@src/store/store.me";
import { useMillesime } from "@src/store/store.millesime";
import { format } from "date-fns";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { CardConformite } from "./CardConformite";
import { EntrepriseAxesConformite } from "./EntrepriseAxesConformite";
import { CardRenouvellement } from "./CardRenouvellement";
import { ControleQuinquennalIndividuel } from "./ControleQuinquennalIndividuel";
import styles from "./Home.module.scss";
import { HomeRightSidebar } from "./HomeRightSidebar";
import { ValidesData } from "./ValidesData";

export function HomeContent() {
  const { me } = useMe();
  const { assujettis } = useAssujetti();
  const { getEntrepriseAvancement } = useEntrepriseAvancement();
  const { entreprisesBySiren, status: entreprisesStatus } = useEntreprises();
  const { hasAllowedRole } = useIsAllowed();
  const { getAssujetisAvancement } = useAssujetisAvancement();
  const { selected: millesime } = useMillesime();

  const { downloadAttestation, getIsAllowedToDownloadAttestation } =
    useDownloadAttestation();

  const exportAssujettis = useCallback(async () => {
    if (!millesime) return;
    try {
      displayInfoToast("Export des personnes assujetties en cours");
      const workbook = await importWorkBookFromFile(
        "modele_personnes_assujetties.xlsx"
      );
      const sheet = workbook.worksheets[0];
      const exportData =
        await GatewayImportExportAssujettiAPI.exportAssujetti(millesime);
      insertDataInSheet(
        exportData.map(transformAPIRowsForToRows),
        sheet,
        assujettiExcelTemplate,
        assujettiExcelExportTemplate
      );
      addAssujettiDataValidationToSheet(sheet, exportData.length);
      await downloadSheetFromWorkbook(
        `export_p_assujetties_${format(new Date(), "dd-MM-yyyy_HH:mm")}.xlsx`,
        workbook
      );
      displaySucessToast("Export des personnes assujetties terminé");
    } catch (e) {
      console.error(e);
      displayErrorToast(
        "Une erreur est survenue lors de l'export des personnes assujetties"
      );
    }
  }, [millesime]);
  const navigate = useNavigate();

  const entreprisesList = useMemo<
    GatewayEntreprise.EntrepriseAControllerResponseType[]
  >(() => Object.values(entreprisesBySiren ?? {}), [entreprisesBySiren]);

  const conformiteOfAllEntreprisesAre100 = useMemo(
    () => entreprisesList.every(getIsAllowedToDownloadAttestation),
    [entreprisesList, getIsAllowedToDownloadAttestation]
  );
  const entreprisesLength = useMemo(
    () => Object.keys(entreprisesBySiren ?? {}).length,
    [entreprisesBySiren]
  );
  const { data: actualites } = useActualite({
    pageSize: 4,
    page: 1,
  });

  return (
    <MainPageTemplate>
      <div className={styles.page}>
        <div className={styles.pageMain}>
          <FormGrid>
            <FormTitle className={styles.title}>
              <h1>Votre conformité</h1>
            </FormTitle>
            <FormRow>
              <FormField>
                {!entreprisesBySiren && <SkeletonCard />}
                {entreprisesBySiren && entreprisesLength > 1 && (
                  <ValidesData
                    getAvancement={getEntrepriseAvancement}
                    values={Object.values(entreprisesBySiren)}
                    title="Entreprises validées"
                  />
                )}
                {entreprisesBySiren && entreprisesLength <= 1 && (
                  <EntrepriseAxesConformite
                    entreprise={Object.values(entreprisesBySiren)[0]}
                  />
                )}
              </FormField>
              <FormField>
                <ValidesData
                  isPerson
                  getAvancement={getAssujetisAvancement}
                  values={assujettis ? Object.values(assujettis) : undefined}
                  icon={<RightArrow />}
                  title="Personnes validées"
                  linkLabel="Ajouter des personnes assujetties"
                  link="/personnes-assujetties"
                  downloadLabel="Télécharger la liste"
                  downloadIcon={<Download />}
                  onDownload={() => {
                    return exportAssujettis();
                  }}
                />
              </FormField>
            </FormRow>
            <ControleQuinquennalIndividuel />
            {entreprisesStatus === StoreEntryFetchStatus.Loaded &&
              !conformiteOfAllEntreprisesAre100 && (
                <FormRow>
                  <FormField>
                    <CardConformite />
                  </FormField>
                </FormRow>
              )}
            {entreprisesStatus === StoreEntryFetchStatus.Loaded &&
              conformiteOfAllEntreprisesAre100 && (
                <FormRow>
                  <FormField>
                    <DownloadAttestationCard
                      onDownload={
                        entreprisesList.length === 1
                          ? downloadAttestation(entreprisesList[0])
                          : () => navigate("/societes")
                      }
                    />
                  </FormField>
                </FormRow>
              )}
            {hasAllowedRole(
              GatewayContact.ContactRoleUtilisateur.ADMINISTRATEUR
            ) &&
              me &&
              me.entreprisePrincipale.etat ===
                GatewayEntreprise.EtatEntreprise.A_RENOUVELER && (
                <FormRow>
                  <FormField>
                    <CardRenouvellement
                      millesime={me.entreprisePrincipale.millesimeActuel}
                    />
                  </FormField>
                </FormRow>
              )}
            <CmsHeader title="S'informer" to="/actualites" />
            <ActualiteList variant="bento" actualites={actualites} />
            <LeSaviezVous />
          </FormGrid>
        </div>

        <HomeRightSidebar />
      </div>
    </MainPageTemplate>
  );
}
export function Home() {
  return (
    <PrismicProvider client={prismicClient}>
      <HomeContent />
    </PrismicProvider>
  );
}
